
interface ArrayConstructor
{
	/** Returns if two array are equals */
	equals(array1: any[], array2: any[]): boolean;

	/** Returns intersected items */
	intersect(array1: any[], array2: any[]): any[];
}

interface Array<T>
{
	/** This method return first element of array */
	first(): T;

	/** This method return last element of array */
	last(): T;

	/** This method returns an array distincted */
	distinct(): T[];

	/** This method returns an array of object distincted by specified property (Example: array.DistinctByProperty(k => k.Name)) */
	distinctByProperty<U>(callbackfn: (value: T, index: number, array: T[]) => U, thisArg?: any): T[];

	/** This method returns an array of specified property (Example: array.Select(k => k.Name) returns string[] array) */
	select<U>(callbackfn: (value: T, index: number, array: T[]) => U, thisArg?: any): U[];

	/** This method returns an array of specified property, but distincted (Example: array.DistinctSelect(k => k.Name)) */
	distinctSelect<U>(callbackfn: (value: T, index: number, array: T[]) => U, thisArg?: any): U[];

	/** This method groups an array group by specified property (Example: array.GroupBy(k => k.Name)) */
	groupBy<U, V>(callbackfn: (value: T, index: number, array: T[]) => U, thisArg?: any): Map<U, V>;

	/** This method returns an array filtered by specified condition (Example: array.GroupBy(k => k.Name == "Pumo")) */
	where<U>(callbackfn: (value: T, index: number, array: T[]) => U, thisArg?: any): T[];

	/** This method sorts an array (ascendent) by specified properties */
	sortAsc(...properties: string[]): any[];

	/** This method sorts an array (discendent) by specified properties */
	sortDesc(...properties: string[]): any[];

	/** This method sorts an array by multiple properties */
	sortBy(properties: string[], ascending?: boolean): any[];

	/** This method turns a string array into number array */
	toNumberArrayList(): number[];

	/** This method turns a any array into string array */
	toStringArrayList(): string[];

	/** This method determines whether all elements of a sequence satisfy a condition */
	all<U>(callbackfn: (value: T, index: number, array: T[]) => U, thisArg?: any): boolean;

	/** This method determines whether any element of a sequence exists or satisfies a condition */
	any<U>(callbackfn: (value: T, index: number, array: T[]) => U, thisArg?: any): boolean;

	/** This method returns the max element of an array */
	maxElement<U>(callbackfn: (value: T, index: number, array: T[]) => U, thisArg?: any): T;

	/** This method returns the min element of an array */
	minElement<U>(callbackfn: (value: T, index: number, array: T[]) => U, thisArg?: any): T;

	/** This method returns the max value of an array */
	max<U>(callbackfn?: (value: T, index: number, array: T[]) => U, thisArg?: any): number;

	/** This method returns the min value of an array */
	min<U>(callbackfn?: (value: T, index: number, array: T[]) => U, thisArg?: any): number;

	/** This method returns the average value of an array */
	avg<U>(propertySelector?: (value: T) => U, thisArg?: any): number;

	/** This method bypasses a specified number of elements in a sequence and then returns the remaining elements */
	skip<T>(index: number): T[];

	/** This method returns the sum of an array of numbers */
	sum(): number;

	/** This method returns the sum of a specified property */
	sum<U>(propertySelector?: (value: T) => U, thisArg?: any): number;

	/** This method turns only 'number' elements */
	take<T>(number: number): T[];

	/** This method removes an element from an array */
	delete(value: any): void;

	/** This method removes an element from an array */
	deleteItem(item: any, key: string): void;

	/** This method removes an element from an array by condition */
	deleteBy<U>(callbackfn?: (value: T, index: number, array: T[]) => U, thisArg?: any): void;

	/** This method removes all elements from an array by condition */
	deleteAllBy<U>(callbackfn?: (value: T, index: number, array: T[]) => U, thisArg?: any): void;

	/** This method draws an array into a comma separated list. Ex: item1, item2 */
	toCommaSeparatedList(addSpaceAfterComma?: boolean): string;

	/** This method adds an array to another array */
	pushRange(arrayToAdd: any[]): void;

	shuffle(): void;

	intersect<T>(array: T[]): T[];

	moveElement(oldIndex: number, newIndex: number): void;
}


Array.prototype.first = function ()
{
	if (Array.isArray(this))
		return this[0];

	return null;
}

Array.prototype.last = function ()
{
	if (Array.isArray(this))
		return this[this.length - 1];

	return null;
}

Array.prototype.distinct = function (this)
{
	if (Array.isArray(this))
		return [...new Set(this)];

	return [];
}

Array.prototype.distinctSelect = function (callbackfn: (value: any, index: number, array: any[]) => any)
{
	if (Array.isArray(this))
		return [...new Set(this.map(callbackfn))];

	return [];
}

Array.prototype.distinctByProperty = function (callbackfn: (value: any, index: number, array: any[]) => any)
{
	if (Array.isArray(this))
	{
		let arrayResult: any[] = [];
		let arrayMap = new Map();
		let property = callbackfn.toString().split(".")[1];

		for (let item of this)
		{
			let value: any;
			for (let key in item)
			{
				if (key == property)
					value = item[key];
			}

			if (!arrayMap.has(value))
			{
				arrayMap.set(value, true);
				arrayResult.push(item);
			}
		}
		return arrayResult;
	}
	return [];
}

Array.prototype.select = function (callbackfn: (value: any, index: number, array: any[]) => any)
{
	if (Array.isArray(this))
		return this.map(callbackfn);

	return [];
}

Array.prototype.groupBy = function (callbackfn: (value: any, index: number, array: any[]) => any)
{
	if (Array.isArray(this))
	{
		let property = callbackfn.toString().split(".")[1];
		let result = this.reduce(function (groups, item)
		{
			const val = item[property];
			groups[val] = groups[val] || [];
			groups[val].push(item);
			return groups;
		}, {});

		let map = new Map();
		for (let res in result)
			map.set(res, result[res]);

		return map;
	}
	return new Map();
}

Array.prototype.where = function (callbackfn: (value: any, index: number, array: any[]) => any)
{
	if (Array.isArray(this))
		return this.filter(callbackfn);

	return [];
}

Array.prototype.all = function (callbackfn: (value: any, index: number, array: any[]) => any)
{
	if (Array.isArray(this))
		return this.every(callbackfn);

	return false;
}

Array.prototype.any = function (callbackfn: (value: any, index: number, array: any[]) => any)
{
	if (Array.isArray(this))
		return this.some(callbackfn);

	return false;
}

Array.prototype.maxElement = function (callbackfn: (value: any, index: number, array: any[]) => any)
{
	if (Array.isArray(this))
	{
		let property = callbackfn.toString().split(".")[1];
		let maxValue = this.reduce((oa, u) => Math.max(oa, u[property]), 0);
		return this.filter(k => k[property] == maxValue)[0];
	}

	return null;
}

Array.prototype.minElement = function (callbackfn: (value: any, index: number, array: any[]) => any)
{
	if (Array.isArray(this))
	{
		let property = callbackfn.toString().split(".")[1];
		let minValue = this.reduce((oa, u) => Math.min(oa, u[property]), Number.MAX_VALUE);
		return this.filter(k => k[property] == minValue)[0];
	}

	return null;
}

Array.prototype.max = function (callbackfn?: (value: any, index: number, array: any[]) => any)
{
	if (Array.isArray(this))
	{
		if (callbackfn != null)
		{
			let property = callbackfn.toString().split(".")[1];
			return this.reduce((oa, u) => Math.max(oa, u[property]), 0);
		}
		else
			return Math.max.apply(null, this);
	}

	return 0;
};

Array.prototype.min = function (callbackfn?: (value: any, index: number, array: any[]) => any)
{
	if (Array.isArray(this))
	{
		if (callbackfn != null)
		{
			let property = callbackfn.toString().split(".")[1];
			return this.reduce((oa, u) => Math.min(oa, u[property]), Number.MAX_VALUE);
		}
		else
			return Math.min.apply(null, this);
	}

	return 0;
};

Array.prototype.skip = function (index: number)
{
	if (Array.isArray(this))
		return this.filter((u, i) => i >= index);

	return this;
}

Array.prototype.sum = function (propertySelector?: (value: any) => any)
{
	if (Array.isArray(this))
	{
		if (propertySelector == null)
			return this.reduce((ty, u) => ty + u, 0);
		else
		{
			let result: number = 0;
			this.forEach(k => result += propertySelector(k));
			return result;
		}
	}

	return 0;
}

Array.prototype.avg = function (propertySelector?: (value: any) => any)
{
	if (Array.isArray(this))
	{
		if (propertySelector == null)
			return this.sum() / this.length;
		else
		{
			let sum = this.sum(propertySelector);
			return sum / this.length;
		}
	}

	return 0;
}

Array.prototype.take = function (number: number)
{
	if (Array.isArray(this))
		return this.filter((u, i) => i < number);

	return this;
}

Array.prototype.shuffle = function ()
{
	let currentIndex = this.length, randomIndex;
	while (currentIndex > 0)
	{
		randomIndex = Math.floor(Math.random() * currentIndex);
		currentIndex--;
		[this[currentIndex], this[randomIndex]] = [this[randomIndex], this[currentIndex]];
	}
	return this;
}

Array.prototype.intersect = function (array: any[])
{
	let intersectedArray = this.filter(k => array.includes(k));
	return intersectedArray;
}

Array.prototype.moveElement = function (oldIndex: number, newIndex: number)
{
	if (newIndex >= this.length)
	{
		var k = newIndex - this.length + 1;
		while (k--)
			this.push(undefined);
	}
	this.splice(newIndex, 0, this.splice(oldIndex, 1)[0]);
}

//#region Sort by
function dynamicSort(property: string)
{
	var sortOrder = 1;
	if (property[0] === "-")
	{
		sortOrder = -1;
		property = property.substr(1);
	}

	return function (a: any, b: any)
	{
		let aProperty = a[property];
		let bProperty = b[property];

		if (typeof (aProperty) == "string")
			aProperty = aProperty.toLowerCase();

		if (typeof (bProperty) == "string")
			bProperty = bProperty.toLowerCase();

		var result = (aProperty < bProperty) ? -1 : (aProperty > bProperty) ? 1 : 0;

		//#region Manage Empty & Null
		if ((typeof (aProperty) == "string" && aProperty == "") || (typeof (bProperty) == "string" && bProperty == ""))
			result = (aProperty == "") ? 1 : -1;

		if (aProperty == null || bProperty == null)
			result = (sortOrder == 1) ? ((aProperty == null) ? 1 : -1) : ((aProperty == null) ? -1 : 1);
		//#endregion

		return result * sortOrder;
	}
}

function dynamicSortMultiple()
{
	var props = arguments;
	return function (obj1: any, obj2: any)
	{
		var i = 0, result = 0, numberOfProperties = props.length;
		while (result === 0 && i < numberOfProperties)
		{
			result = dynamicSort(props[i])(obj1, obj2);
			i++;
		}
		return result;
	}
}

Array.prototype.sortBy = function (properties: string[], ascending?: boolean)
{
	if (Array.isArray(this))
	{
		if (properties != null && properties.length > 0)
		{
			if (ascending != null && !ascending)
				properties = properties.map(k => { return (!k.startsWith("-")) ? "-" + k : k });

			this.sort(dynamicSortMultiple.apply(null, properties as any));
		}
		else
			internalSortWithoutProperties(this, (ascending == null) ? true : ascending);
	}
	return this;
}

Array.prototype.sortAsc = function (...properties: string[])
{
	return this.sortBy(properties, true);
}

Array.prototype.sortDesc = function (...properties: string[])
{
	return this.sortBy(properties, false);
}

function internalSortWithoutProperties(array: any[], ascending: boolean)
{
	array.sort((a: any, b: any) => 
	{
		if (ascending)
		{
			if (typeof (a) == "number")
			{
				const diff = a - b;
				if (diff)
					return diff;
			}
			else if (typeof (a) == "string")
			{
				// Try to catch date
				let tryDate = new Date(a);
				if (!isNaN(tryDate.getTime()))
					return new Date(a).getTime() - new Date(b).getTime();
				else
					return a.localeCompare(b);
			}
			else if (Object.prototype.toString.call(a) === '[object Date]')
				return new Date(a).getTime() - new Date(b).getTime();

			return 0;
		}
		else
		{
			if (typeof (b) == "number")
			{
				const diff = b - a;
				if (diff)
					return diff;
			}
			else if (typeof (b) == "string")
			{
				// Try to catch date
				let tryDate = new Date(b);
				if (!isNaN(tryDate.getTime()))
					return new Date(b).getTime() - new Date(a).getTime();
				else
					return b.localeCompare(a);
			}
			else if (Object.prototype.toString.call(b) === '[object Date]')
				return new Date(b).getTime() - new Date(a).getTime();

			return 0;
		}
	});
}
//#endregion

Array.prototype.toNumberArrayList = function ()
{
	if (Array.isArray(this))
		return this.map((value) => { return Number(value) });

	return [];
}

Array.prototype.toStringArrayList = function ()
{
	if (Array.isArray(this))
		return this.map((value) => { return String(value) });

	return [];
}

Array.prototype.delete = function (value: any)
{
	if (Array.isArray(this))
	{
		let index = this.indexOf(value);
		if (index != -1)
			this.splice(index, 1);
	}
}

Array.prototype.deleteItem = function (item: any, key: string)
{
	if (Array.isArray(this))
	{
		let index = this.map(k => k[key]).indexOf(item[key]);
		if (index != -1)
			this.splice(index, 1);
	}
}

Array.prototype.deleteBy = function (callbackfn: (value: any, index: number, array: any[]) => any)
{
	if (Array.isArray(this))
	{
		let itemToDelete = this.filter(callbackfn)[0];
		if (itemToDelete != null)
			this.delete(itemToDelete);
	}
}

Array.prototype.deleteAllBy = function (callbackfn: (value: any, index: number, array: any[]) => any)
{
	if (Array.isArray(this))
	{
		let itemToDeleteList = this.filter(callbackfn);
		for (let itemToDelete of itemToDeleteList)
		{
			if (itemToDelete != null)
				this.delete(itemToDelete);
		}
	}
}

Array.prototype.toCommaSeparatedList = function (addSpaceAfterComma: boolean = true)
{
	if (Array.isArray(this))
	{
		let result = "";
		for (let item of this)
			result += item.toString() + ((addSpaceAfterComma) ? ", " : ",");

		return result.substring(0, result.length - ((addSpaceAfterComma) ? 2 : 1));
	}
	else
		return "";
}

Array.prototype.pushRange = function (arrayToAdd: any[])
{
	if (Array.isArray(this))
	{
		for (let element of arrayToAdd)
			this.push(element);
	}
}

Array.equals = function (array1: any[], array2: any[]): boolean
{
	if (Array.isArray(array1) && Array.isArray(array2))
	{
		let array1Length = array1.length;

		if (array1 == null || array2 == null) return false;
		if (array1Length != array2.length) return false;

		let arr1 = array1.concat().sort();
		let arr2 = array2.concat().sort();

		for (let i = 0; i < arr1.length; ++i)
		{
			if (arr1[i] !== arr2[i])
				return false;
		}
		return true;
	}
	return false;
}

Array.intersect = function (array1: any[], array2: any[]): any[]
{
	if (Array.isArray(array1) && Array.isArray(array2))
		return array1.filter(k => array2.includes(k));

	return [];
}

